import {getKeysUnsafe} from '@raison/toolbox';
import {produce} from 'immer';
import {Dispatch, SetStateAction} from 'react';
import {Merged} from '~/shared/utils/types';
import {ImmerSetState} from './useImmerState';

export type FormValuesShape = Record<string, unknown>;

export type FormState<V extends FormValuesShape> = ReturnType<typeof FormState<V>>;
export const {FormState} = class FormState<V extends FormValuesShape> {
    static FormState = <V extends FormValuesShape>(values: V) => ({...new FormState(values)});

    constructor(
        public values: V //
    ) {
        this.flags = Object.fromEntries(
            getKeysUnsafe(values).map(key => [
                key,
                {
                    wasModified: 0,
                    wasBlurred: 0,
                    completed: false,
                },
            ])
        ) satisfies Flags<FormValuesShape> as Flags<V>;
    }

    flags: Flags<V>;

    serverIssues: Issues<V> = {};
    submitInitAt: number = 0;
    submitDoneAt: number = 0;
};

export type Flags<T = FormValuesShape> = {
    [K in keyof T]: {
        wasModified: number;
        wasBlurred: number;
        completed: boolean;
    };
};

export type Issues<T = FormValuesShape> = {
    [K in keyof T]?: string | undefined;
};

export interface AsyncValidationResult<V> {
    issues: Issues<V>;
    checkedAt: number;
}

export type FormStateValidated<V extends FormValuesShape, VV extends {[K in keyof V]?: any} = V> = {
    issues: Issues<V>;
    hasServerIssues: boolean;
    isSubmitting: boolean;
} & (
    | ({
          hasIssues: true;
      } & FormState<V>)
    | ({
          hasIssues: false;
      } & Merged<FormState<V>, {values: Merged<V, VV>}>)
);

export function defineFormStateValidation<
    V extends FormValuesShape,
    VV extends {[K in keyof V]?: any},
    A extends any[] = [],
>(validate: (values: V, ...args: A) => {issues: Issues<V>; values: VV}) {
    return (state: FormState<V>, ...args: A): FormStateValidated<V, VV> => {
        return validateFormState(state, values => validate(values, ...args));
    };
}

export function validateFormState<V extends FormValuesShape, VV extends {[K in keyof V]?: any}>(
    formState: FormState<V>,
    validate: (values: V) => {issues: Issues<V>; values: VV}
): FormStateValidated<V, VV> {
    const {values: validValues, issues} = validate(formState.values);
    const keys = getKeysUnsafe(formState.values);
    const hasIssues = keys.map(key => issues[key]).filter(Boolean).length > 0;

    const hasServerIssues = keys.some(key => {
        const serverIssue = formState.serverIssues[key];
        if (!serverIssue) return false;

        const flag = formState.flags[key] ?? {};
        const modifiedAt = flag.wasModified ?? 0;

        return modifiedAt < formState.submitDoneAt;
    });

    const isSubmitting = formState.submitInitAt > formState.submitDoneAt;
    const computedState = {...formState, isSubmitting, issues, hasServerIssues};
    if (hasIssues) {
        return {...computedState, hasIssues};
    } else {
        return {...computedState, hasIssues, values: {...formState.values, ...validValues}};
    }
}

export function connectFieldWith<Fields extends FormValuesShape>(
    state: Pick<FormStateValidated<Fields>, 'flags' | 'issues' | 'serverIssues' | 'submitInitAt' | 'submitDoneAt'>,
    onBlur: (field: keyof Fields) => void
) {
    return (fieldName: keyof Fields, {defaultHelperText}: {defaultHelperText?: string} = {}) => {
        const flag = state.flags[fieldName] ?? {};
        const modifiedAt = flag.wasModified ?? 0;
        const serverIssue = modifiedAt < state.submitDoneAt ? state.serverIssues[fieldName] : undefined;

        const issue = state.issues[fieldName];
        const visibleIssue = (flag.completed ? issue : undefined) || serverIssue;
        const isInvalid = !!visibleIssue;

        return {
            helperText: isInvalid ? visibleIssue : defaultHelperText,
            isInvalid,
            onBlur: () => onBlur(fieldName),
        };
    };
}

export function writeUserInput<V extends FormValuesShape>(formState: FormState<V>, updated: Partial<V>) {
    for (const field of Object.keys(updated) as Array<keyof V>) {
        const flag = formState.flags[field];
        if (updated[field] !== formState.values[field]) {
            flag.wasModified = Date.now();
            delete formState.serverIssues[field];
        }
    }
    formState.values = {...formState.values, ...updated};
}

export function writeFieldBlur<V extends FormValuesShape>(formState: FormState<V>, field: keyof V) {
    const flag = formState.flags[field];
    flag.wasBlurred = Date.now();
    if (flag.wasModified) {
        flag.completed = true;
    }
}

export function writeFieldCompleted<V extends FormValuesShape>(formState: FormState<V>, field: keyof V) {
    const flag = formState.flags[field];
    if (flag.wasModified) {
        flag.completed = true;
    }
}

export function writeFieldsComplete<V extends FormValuesShape>(formState: FormState<V>, ...fields: Array<keyof V>) {
    fields.forEach(field => {
        const flag = formState.flags[field];
        flag.completed = true;
    });
}

export const userInputRecipe =
    <V extends FormValuesShape>(update: Partial<V>) =>
    (formState: FormState<V>): undefined =>
        void writeUserInput(formState, update);

export const fieldBlurRecipe =
    <V extends FormValuesShape>(field: keyof V) =>
    (formState: FormState<V>): undefined =>
        void writeFieldBlur(formState, field);

export const fieldsCompleteRecipe =
    <V extends FormValuesShape>(...fields: Array<keyof V>) =>
    (formState: FormState<V>): undefined =>
        void writeFieldsComplete(formState, ...fields);

export const allFieldsKeys = <V extends FormValuesShape>(formState: FormState<V>): (keyof V)[] => {
    return Object.keys(formState.flags);
};

export const allFieldsCompleteRecipe =
    <V extends FormValuesShape>() =>
    (formState: FormState<V>): undefined =>
        void writeFieldsComplete(formState, ...(Object.keys(formState.values) as Array<keyof V>));

type FormStateReducer<V extends FormValuesShape> = (formState: FormState<V>) => FormState<V>;

export const reduceUserInput =
    <V extends FormValuesShape>(update: Partial<V>): FormStateReducer<V> =>
    formState =>
        produce(formState, userInputRecipe(update));

export const reduceFieldBlur =
    <V extends FormValuesShape>(field: keyof V): FormStateReducer<V> =>
    formState =>
        produce(formState, fieldBlurRecipe(field));

export const reduceFieldsCompeted =
    <V extends FormValuesShape>(...fields: Array<keyof V>): FormStateReducer<V> =>
    formState =>
        produce(formState, fieldsCompleteRecipe(...fields));

export const reduceSubmitInit =
    <V extends FormValuesShape>(): FormStateReducer<V> =>
    formState =>
        produce(formState, draft => {
            draft.submitInitAt = Date.now();
        });

export const reduceSubmitDone =
    <V extends FormValuesShape>(): FormStateReducer<V> =>
    formState =>
        produce(formState, draft => {
            draft.submitDoneAt = Date.now();
        });

export function connectSubmitCallback({
    init: setSubmitStart,
    done: setSubmitEnd,
}: {
    init: (ts: number) => void;
    done: (ts: number) => void;
}) {
    return <A extends any[]>(callback: (...args: A) => Promise<void>) =>
        async (...args: A) => {
            setSubmitStart(Date.now());
            try {
                await callback(...args);
            } finally {
                setSubmitEnd(Date.now());
            }
        };
}

export function connectSubmitToImmerForm(setFormState: ImmerSetState<FormState<any>>) {
    return connectSubmitCallback({
        init: ts => setFormState(form => void (form.submitInitAt = ts)),
        done: ts => setFormState(form => void (form.submitDoneAt = ts)),
    });
}

export function connectSubmitToForm(setFormState: Dispatch<SetStateAction<FormState<any>>>) {
    return connectSubmitCallback({
        init: ts => setFormState(form => ({...form, submitInitAt: ts})),
        done: ts => setFormState(form => ({...form, submitDoneAt: ts})),
    });
}

export function markAllFieldsAsCompleted<V extends FormValuesShape>(setFormState: ImmerSetState<FormState<V>>) {
    setFormState(form => {
        for (const field in form.flags) {
            form.flags[field].completed = true;
        }
    });
}
